import { Fragment, useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { downloadXlsx } from '../../lib/downloadXlsx'
import { Card } from '../../components/ui/Card'
import { AntetFix } from '../../components/ui/AntetFix'
import { QuickDateRange } from '../../components/ui/DateField'
import { CostsCard } from './CostsCard'
import { NoteEaPlata } from './NoteEaPlata'

const lei = (n: number) =>
  n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const nr = (n: number) => n.toLocaleString('ro-RO')
const d = (s: string) => new Date(s).toLocaleDateString('ro-RO')

/** Starea descarcarii din Seller Center, pe ecran in romana (cheile vin de la server). */
type StareDescarcare = 'idle' | 'running' | 'otp_required' | 'done' | 'error'
const STARE_DESCARCARE: Record<StareDescarcare, string> = {
  idle: 'în așteptare',
  running: 'rulează',
  otp_required: 'așteaptă codul OTP',
  done: 'terminată',
  error: 'eroare',
}

interface Settlement {
  id: number
  dpId: string
  payoutDate: string
  periodStart: string
  periodEnd: string
  net: number
  currency: string
  lines: number
  sourceFile: string
}
interface SettlementsResp {
  settlements: Settlement[]
  byFraction: { fractionType: string; count: number; value: number }[]
  totalNet: number
}
interface ReconResp {
  summary: {
    totalOrders: number
    noInvoice: number
    refundedWash: number
    notInEa: number
    notInSpv: number
    eaUnknown: number
    spvUnknown: number
    valueMismatch: number
    allOk: number
    cashingTotal: number
  }
  exceptions: {
    orderId: string
    cashingNet: number
    orderStatus: string | null
    invNo: string | null
    invTotal: number | null
    category: string | null
  }[]
  bank: {
    dpId: string
    payoutDate: string
    net: number
    bankAmount: number | null
    processor: string | null
    matched: boolean
  }[]
  heyBlue: { count: number; total: number }
}
interface PayoutsResp {
  payouts: {
    payoutId: string
    payoutDate: string
    processor: string | null
    sales: number | null
    commissions: number | null
    fbe: number | null
    ads: number | null
    vouchers: number | null
    otherInv: number | null
    courier: number | null
    otherDoc: number | null
    payments: number | null
    total: number
    bankAmount: number | null
    matched: boolean
    /** Cand s-a descarcat fisierul cu notele pentru EA (null = inca nu). */
    eaExportatLa: string | null
  }[]
  totals: { count: number; total: number; matched: number }
}
interface ImportResult {
  file: string
  type: string
  docId?: string
  docs?: number
  lines: number
  replaced: boolean
  detail?: string
}
interface ScraperStatus {
  state: StareDescarcare
  step: string
  filesDownloaded: number
  filesImported: number
  filesReplaced: number
  linesImported: number
  errors: number
  diagnostics: string[]
}
interface ImportResp {
  files: number
  imported: number
  skipped: number
  lines: number
  results: ImportResult[]
}

interface PayoutDetailResp {
  hasLines: boolean
  summary: { docCode: string; label: string; count: number; total: number }[]
  orders: {
    docCode: string
    orderId: string | null
    amount: number
    customerName: string | null
    orderDate: string | null
    status: number | null
  }[]
}

// Buton: porneste in fundal descarcarea fiselor de cont lipsa (payout-uri cu referinta
// dar fara detaliu). Ruleaza in lot pe server; aici doar declansam + aratam cate au ramas.
function BackfillPayouts() {
  const qc = useQueryClient()
  const [msg, setMsg] = useState<string | null>(null)
  const { data } = useQuery<{ remaining: number }>({
    queryKey: ['emag-payout-backfill-status'],
    queryFn: async () => (await api.get('/emag/payouts/backfill/status')).data,
    refetchInterval: 30_000,
  })
  const remaining = data?.remaining ?? 0
  const onStart = async () => {
    setMsg('Pornit — rulează în fundal…')
    try {
      const r = (await api.post('/emag/payouts/backfill')).data as { remaining: number }
      setMsg(
        `Pornit pentru ${nr(r.remaining)} payout-uri. Se umple treptat (reîncarcă peste câteva minute).`,
      )
      await qc.invalidateQueries({ queryKey: ['emag-payout-backfill-status'] })
    } catch {
      setMsg('Eroare la pornire.')
    }
  }
  if (remaining === 0 && !msg) return null
  return (
    <div className="flex items-center gap-2">
      {remaining > 0 && (
        <button
          onClick={() => void onStart()}
          title="Descarcă fișele de cont lipsă pentru payout-urile fără detaliu"
          className="rounded-md border border-b4l-border bg-white px-3 py-1.5 text-xs font-semibold text-b4l-text shadow-sm transition hover:border-b4l-blue hover:text-b4l-blue-dark"
        >
          Sincronizează detaliile lipsă ({nr(remaining)})
        </button>
      )}
      {msg && <span className="text-[11px] text-b4l-text-muted">{msg}</span>}
    </div>
  )
}

// Drill-down: comenzile (incasari) dintr-un payout + sumar; descarcare on-demand a fisei.
function PayoutDetail({ payoutId }: { payoutId: string }) {
  const qc = useQueryClient()
  const [fetching, setFetching] = useState(false)
  const [fetchMsg, setFetchMsg] = useState<string | null>(null)
  const { data, isLoading } = useQuery<PayoutDetailResp>({
    queryKey: ['emag-payout-detail', payoutId],
    queryFn: async () => (await api.get(`/emag/payouts/${payoutId}/detail`)).data,
  })
  // Descarcarea dureaza ~60-90s (Playwright + mii de randuri) — adesea peste timeout-ul
  // proxy-ului. Importul se finalizeaza oricum in fundal, deci la eroare/timeout facem
  // polling pe detaliu pana apar liniile, in loc sa aratam fals "Eroare".
  const pollUntilLines = async (tries = 12): Promise<boolean> => {
    for (let i = 0; i < tries; i++) {
      await new Promise((r) => setTimeout(r, 8_000))
      try {
        const d = (await api.get(`/emag/payouts/${payoutId}/detail`)).data as PayoutDetailResp
        if (d?.hasLines) {
          await qc.invalidateQueries({ queryKey: ['emag-payout-detail', payoutId] })
          return true
        }
      } catch {
        /* mai incercam */
      }
    }
    return false
  }
  const onFetch = async () => {
    setFetching(true)
    setFetchMsg('Se descarcă… (poate dura ~1 minut)')
    try {
      const r = (await api.post(`/emag/payouts/${payoutId}/fetch`, undefined, { timeout: 180_000 }))
        .data as { ok: boolean; error?: string; imported?: number }
      if (r.ok) {
        setFetchMsg(`Descărcat: ${nr(r.imported ?? 0)} linii`)
        await qc.invalidateQueries({ queryKey: ['emag-payout-detail', payoutId] })
      } else setFetchMsg(r.error ?? 'Eroare')
    } catch {
      // Timeout proxy/HTTP — importul ruleaza in fundal; asteptam sa apara liniile.
      setFetchMsg('Se finalizează în fundal…')
      const ok = await pollUntilLines()
      setFetchMsg(ok ? null : 'Încă se procesează — reîncarcă pagina în ~1 minut.')
    } finally {
      setFetching(false)
    }
  }
  if (isLoading) return <p className="text-[11px] text-b4l-text-muted">Se încarcă detaliul…</p>
  if (!data?.hasLines) {
    return (
      <div className="text-[11px] text-b4l-text-muted">
        Detaliul (comenzile) acestui payout nu e încă descărcat.{' '}
        <button
          onClick={() => void onFetch()}
          disabled={fetching}
          className="font-semibold text-b4l-blue hover:underline disabled:opacity-60"
        >
          {fetching ? 'Se descarcă…' : 'Descarcă detaliul'}
        </button>
        {fetchMsg && <span className="ml-2">{fetchMsg}</span>}
      </div>
    )
  }
  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <button
          onClick={() =>
            void downloadXlsx(`/emag/payouts/${payoutId}/export`, `payout-${payoutId}.xlsx`)
          }
          className="rounded-md border border-b4l-border bg-white px-3 py-1 text-[11px] font-semibold text-b4l-text shadow-sm transition hover:border-b4l-blue hover:text-b4l-blue-dark"
        >
          Export Excel (acest payout)
        </button>
      </div>
      <NoteEaPlata payoutId={payoutId} />
      {(() => {
        // `total` = suma SEMNATA din fisa de cont (eMAG: incasari cu minus, costuri/restituiri
        // cu plus). Contributia la plata, din perspectiva sellerului, = -total (incasari +,
        // costuri −). „Compensare payout" (ZP) e REZULTATUL, nu o componenta — il scoatem din
        // chips si il afisam ca total de verificare.
        const comps = data.summary.filter((s) => s.docCode !== 'ZP')
        const computed = comps.reduce((a, s) => a - s.total, 0)
        return (
          <div className="space-y-1">
            <div className="flex flex-wrap gap-1.5">
              {comps.map((s) => {
                const contrib = -s.total
                return (
                  <span
                    key={s.docCode}
                    className="rounded border border-b4l-border bg-white px-2 py-0.5 text-[11px]"
                  >
                    {s.label}: <span className="font-semibold">{nr(s.count)}</span> ·{' '}
                    <span className={contrib < 0 ? 'text-red-600' : 'text-green-700'}>
                      {contrib < 0 ? '−' : '+'}
                      {lei(Math.abs(contrib))} lei
                    </span>
                  </span>
                )
              })}
            </div>
            <div className="text-[11px] text-b4l-text-muted">
              Σ componente ={' '}
              <span className="font-semibold text-b4l-text">{lei(computed)} lei</span> = Total plată
              (suma efectiv virată de eMAG în cont, într-un singur transfer)
            </div>
          </div>
        )
      })()}
      <div className="max-h-72 overflow-y-auto">
        <table className="w-full text-[11px]">
          <thead className="sticky top-0 bg-b4l-bg-soft text-left text-b4l-text-muted">
            <tr>
              <th className="py-1 pr-3">Comandă</th>
              <th className="py-1 pr-3">Tip</th>
              <th className="py-1 pr-3">Client</th>
              <th className="py-1 pr-3">Data</th>
              <th className="py-1 pr-3 text-right">Încasat</th>
            </tr>
          </thead>
          <tbody>
            {data.orders.map((o, i) => (
              <tr key={(o.orderId ?? '') + i} className="border-t border-b4l-border/50">
                <td className="py-1 pr-3 font-mono">{o.orderId ?? '—'}</td>
                <td className="py-1 pr-3 text-b4l-text-muted">
                  {o.docCode === 'KX' ? 'ramburs' : 'card'}
                </td>
                <td className="py-1 pr-3">{o.customerName ?? '—'}</td>
                <td className="py-1 pr-3">{o.orderDate ? d(o.orderDate) : '—'}</td>
                <td className="py-1 pr-3 text-right">{lei(o.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function IncasariEmagPage() {
  const qc = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [report, setReport] = useState<ImportResp | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Implicit: anul curent (ca sa nu arate tot istoricul de payout-uri din 2018).
  const thisYear = new Date().getFullYear()
  const [from, setFrom] = useState(`${thisYear}-01-01`)
  const [to, setTo] = useState(`${thisYear}-12-31`)
  const [tab, setTab] = useState<'date' | 'import'>('date')
  const [showDec, setShowDec] = useState(false)
  const [showVerif, setShowVerif] = useState(false)
  const [expandedPayouts, setExpandedPayouts] = useState<Set<string>>(new Set())
  const togglePayout = (id: string) =>
    setExpandedPayouts((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const qs = () => {
    const p = new URLSearchParams()
    if (from) p.set('from', from)
    if (to) p.set('to', to)
    const s = p.toString()
    return s ? `?${s}` : ''
  }
  const tabCls = (on: boolean) =>
    `-mb-px border-b-2 px-4 py-2 text-sm font-semibold transition ${
      on
        ? 'border-b4l-blue text-b4l-blue-dark'
        : 'border-transparent text-b4l-text-muted hover:text-b4l-text'
    }`

  const { data: settle } = useQuery<SettlementsResp>({
    queryKey: ['emag-settlements', from, to],
    queryFn: async () => (await api.get(`/emag/settlements${qs()}`)).data,
  })
  const { data: recon } = useQuery<ReconResp>({
    queryKey: ['emag-reconcile', from, to],
    queryFn: async () => (await api.get(`/emag/settlements/reconcile${qs()}`)).data,
  })
  const { data: payouts } = useQuery<PayoutsResp>({
    queryKey: ['emag-payouts', from, to],
    queryFn: async () => (await api.get(`/emag/payouts${qs()}`)).data,
  })

  const [scanning, setScanning] = useState(false)
  const [scanMsg, setScanMsg] = useState<string | null>(null)
  const [otp, setOtp] = useState('')

  const { data: scraper } = useQuery<ScraperStatus>({
    queryKey: ['emag-scraper-status'],
    queryFn: async () => (await api.get('/emag/scraper/status')).data,
    refetchInterval: (q) => {
      const st = (q.state.data as ScraperStatus | undefined)?.state
      return st === 'running' || st === 'otp_required' ? 2500 : false
    },
  })

  const runScraper = async (pages: number) => {
    setOtp('')
    await api.post('/emag/scraper/run', { pages })
    await qc.invalidateQueries({ queryKey: ['emag-scraper-status'] })
  }
  const sendOtp = async () => {
    if (!otp.trim()) return
    await api.post('/emag/scraper/otp', { code: otp.trim() })
    setOtp('')
    await qc.invalidateQueries({ queryKey: ['emag-scraper-status'] })
  }
  const scraperBusy = scraper?.state === 'running' || scraper?.state === 'otp_required'

  // Cand scraper-ul termina -> reimprospateaza datele.
  useEffect(() => {
    if (scraper?.state === 'done') {
      void qc.invalidateQueries({ queryKey: ['emag-settlements'] })
      void qc.invalidateQueries({ queryKey: ['emag-fees-summary'] })
      void qc.invalidateQueries({ queryKey: ['emag-reconcile'] })
    }
  }, [scraper?.state, qc])

  const onScanInbox = async () => {
    setScanning(true)
    setScanMsg(null)
    try {
      const r = (await api.post('/emag/desf/scan-inbox')).data as {
        filesRecognized: number
        filesImported: number
        filesReplaced: number
        linesImported: number
        errors: number
        diagnostics: string[]
      }
      setScanMsg(
        `Scanare e-mail: ${r.filesRecognized} fișiere recunoscute · ${r.filesImported} noi · ${r.filesReplaced} actualizate · ${r.linesImported.toLocaleString('ro-RO')} linii` +
          (r.errors ? ` · ${r.errors} erori` : '') +
          (r.diagnostics.length ? ` — ${r.diagnostics.slice(0, 3).join('; ')}` : ''),
      )
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['emag-settlements'] }),
        qc.invalidateQueries({ queryKey: ['emag-fees-summary'] }),
        qc.invalidateQueries({ queryKey: ['emag-reconcile'] }),
      ])
    } catch (e) {
      const err = e as { response?: { data?: { error?: { message?: string } } } }
      setScanMsg(err.response?.data?.error?.message ?? 'Eroare la scanarea inboxului')
    } finally {
      setScanning(false)
    }
  }

  const onUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    setUploading(true)
    setError(null)
    setReport(null)
    try {
      const fd = new FormData()
      Array.from(files).forEach((f) => fd.append('files', f))
      const resp = await api.post<ImportResp>('/emag/desf/import', fd, { timeout: 5 * 60_000 })
      setReport(resp.data)
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['emag-settlements'] }),
        qc.invalidateQueries({ queryKey: ['emag-fees-summary'] }),
      ])
    } catch (e) {
      const err = e as { response?: { data?: { error?: { message?: string } } } }
      setError(err.response?.data?.error?.message ?? 'Eroare la import')
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  // Incasari: pozitiv = bani primiti; Refund = negativ.
  const cashing = settle?.byFraction.filter((f) => !/refund/i.test(f.fractionType)) ?? []
  const refunds = settle?.byFraction.filter((f) => /refund/i.test(f.fractionType)) ?? []
  const refundTotal = refunds.reduce((a, f) => a + f.value, 0)

  return (
    <div className="space-y-3">
      <AntetFix>
        <h1 className="text-lg font-semibold sm:text-xl">eMAG → Încasări &amp; costuri</h1>

        <input
          ref={fileRef}
          type="file"
          multiple
          accept=".xlsx"
          className="hidden"
          onChange={(e) => onUpload(e.target.files)}
        />

        <div className="flex gap-1 border-b border-b4l-border">
          <button type="button" onClick={() => setTab('date')} className={tabCls(tab === 'date')}>
            Date &amp; costuri
          </button>
          <button
            type="button"
            onClick={() => setTab('import')}
            className={tabCls(tab === 'import')}
          >
            Import &amp; sincronizare
          </button>
        </div>
      </AntetFix>

      {tab === 'date' && (
        <Card>
          <div className="mb-2 text-xs font-semibold text-b4l-text-muted">
            Perioadă (după data plății decontului)
          </div>
          <QuickDateRange
            fromValue={from}
            toValue={to}
            onChange={(f, t) => {
              setFrom(f)
              setTo(t)
            }}
          />
        </Card>
      )}

      {tab === 'import' && (
        <>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={onScanInbox}
              disabled={scanning}
              className="rounded-md border border-b4l-border bg-white px-4 py-2 text-sm font-semibold text-b4l-text shadow-sm transition hover:border-b4l-blue hover:text-b4l-blue-dark disabled:opacity-60"
            >
              {scanning ? 'Se scanează…' : 'Scanează email'}
            </button>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="rounded-md border border-b4l-blue bg-b4l-blue px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-b4l-blue-dark disabled:opacity-60"
            >
              {uploading ? 'Se importă…' : 'Import manual (xlsx)'}
            </button>
          </div>

          {scanMsg && (
            <Card>
              <span className="text-sm">{scanMsg}</span>
            </Card>
          )}

          <p className="text-xs text-b4l-text-muted">
            Desfășurătoarele eMAG (decont DP + comision/ads/fulfillment/vouchere) se preiau cu
            scraper-ul, direct din portal (eMAG nu le trimite pe mail) — vezi cardul de mai jos.
            Scanează email e doar plasă de siguranță (dacă redirectezi manual un xlsx pe căsuță);
            Import manual e pentru fișiere xlsx descărcate de tine. Tot importul e idempotent (nu
            dublează).
          </p>

          {/* Scraper portal eMAG (semi-automat cu OTP) */}
          <Card title="Scraper portal eMAG (descărcare automată)">
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => void runScraper(2)}
                disabled={scraperBusy}
                className="rounded-md border border-b4l-blue bg-b4l-blue px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-b4l-blue-dark disabled:opacity-60"
              >
                Descarcă recent (pg 1–2)
              </button>
              <button
                onClick={() => void runScraper(10)}
                disabled={scraperBusy}
                className="rounded-md border border-b4l-border bg-white px-4 py-2 text-sm font-semibold text-b4l-text shadow-sm transition hover:border-b4l-blue hover:text-b4l-blue-dark disabled:opacity-60"
              >
                Import complet (toate paginile)
              </button>
              {scraper && scraper.state !== 'idle' && (
                <span className="text-xs text-b4l-text-muted">
                  Stare: <span className="font-medium">{STARE_DESCARCARE[scraper.state]}</span>
                  {scraper.step ? ` · ${scraper.step}` : ''} · {nr(scraper.filesDownloaded)}{' '}
                  descărcate · {nr(scraper.filesImported + scraper.filesReplaced)} importate
                  {scraper.errors ? ` · ${nr(scraper.errors)} erori` : ''}
                </span>
              )}
            </div>

            {scraper?.state === 'otp_required' && (
              <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-500 bg-amber-50 p-3">
                <span className="text-sm font-medium text-amber-800">
                  eMAG a trimis un cod OTP pe telefon. Introdu-l aici:
                </span>
                <input
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && void sendOtp()}
                  placeholder="cod OTP"
                  className="w-32 rounded border border-b4l-border px-2 py-1 text-sm"
                />
                <button
                  onClick={() => void sendOtp()}
                  className="rounded-md border border-b4l-blue bg-b4l-blue px-3 py-1.5 text-sm font-semibold text-white"
                >
                  Trimite cod
                </button>
              </div>
            )}

            {scraper && scraper.diagnostics.length > 0 && (
              <div className="mt-2 max-h-48 space-y-1 overflow-y-auto rounded border border-b4l-border bg-b4l-bg-soft/40 p-2">
                {scraper.diagnostics.map((dg, i) => (
                  <p key={i} className="break-all text-[10px] text-b4l-text-muted">
                    {dg}
                  </p>
                ))}
              </div>
            )}
            <p className="mt-2 text-[11px] text-b4l-text-muted">
              Scraper-ul se loghează pe portal, descarcă desfășurătoarele (DP → Format standard) și
              le importă. <span className="font-medium">Rulează automat zilnic la 10:00</span> și
              descarcă ce-a apărut nou. eMAG cere OTP doar periodic — cât timp sesiunea e validă
              merge singur; când eMAG re-cere OTP primești o notificare în CRM și faci o singură
              rulare manuală cu butonul Descarcă recent + cod. Pentru prima preluare folosește
              Import complet.
            </p>
          </Card>

          {error && (
            <Card>
              <span className="text-sm text-b4l-red-dark">{error}</span>
            </Card>
          )}

          {report && (
            <Card title="Rezultat import">
              <div className="text-sm">
                {report.imported} fișiere importate · {report.lines.toLocaleString('ro-RO')} linii ·{' '}
                {report.skipped > 0 && (
                  <span className="text-b4l-red-dark">{report.skipped} nerecunoscute</span>
                )}
              </div>
              <div className="mt-2 max-h-48 overflow-y-auto">
                <table className="w-full text-xs">
                  <tbody>
                    {report.results.map((r, i) => (
                      <tr key={i} className="border-b border-b4l-border last:border-0">
                        <td className="py-1 pr-3 font-mono text-[11px]">{r.file}</td>
                        <td className="py-1 pr-3">{r.type}</td>
                        <td className="py-1 pr-3 text-right">{nr(r.lines)} linii</td>
                        <td className="py-1 pr-3">
                          {r.type === 'unsupported' ? (
                            <span className="text-b4l-red-dark">{r.detail ?? 'nerecunoscut'}</span>
                          ) : r.replaced ? (
                            <span className="text-amber-700">înlocuit</span>
                          ) : (
                            <span className="text-b4l-green-dark">nou</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      {tab === 'date' && (
        <>
          {/* Incasari — sumar pe tip fractiune */}
          <Card title="Încasări — sumar (toate deconturile)">
            {!settle || settle.settlements.length === 0 ? (
              <p className="text-sm text-b4l-text-muted">
                Niciun decont importat încă. Folosește butonul de import.
              </p>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {cashing.map((f) => (
                    <div key={f.fractionType} className="rounded-lg border border-b4l-border p-3">
                      <div className="truncate text-xs text-b4l-text-muted">{f.fractionType}</div>
                      <div className="text-lg font-semibold text-b4l-green-dark">
                        {lei(f.value)} lei
                      </div>
                      <div className="text-xs text-b4l-text-muted">{nr(f.count)} linii</div>
                    </div>
                  ))}
                  <div className="rounded-lg border border-b4l-border p-3">
                    <div className="truncate text-xs text-b4l-text-muted">Retururi (Refund)</div>
                    <div className="text-lg font-semibold text-b4l-red-dark">
                      {lei(refundTotal)} lei
                    </div>
                  </div>
                  <div className="rounded-lg border-2 border-b4l-blue bg-b4l-blue-light/20 p-3">
                    <div className="truncate text-xs text-b4l-text-muted">Net încasat (total)</div>
                    <div className="text-lg font-semibold sm:text-xl">
                      {lei(settle.totalNet)} lei
                    </div>
                  </div>
                </div>
              </>
            )}
          </Card>

          {/* Deconturi (DP) — sumar + drill-down */}
          {settle && settle.settlements.length > 0 && (
            <Card title="Deconturi (desfășurătoare DP)">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm">
                  <span className="font-semibold">{nr(settle.settlements.length)}</span> deconturi ·
                  Net total: <span className="font-semibold">{lei(settle.totalNet)} lei</span>
                </div>
                <button
                  onClick={() => setShowDec((v) => !v)}
                  className="text-xs font-semibold text-b4l-blue hover:text-b4l-blue-dark hover:underline"
                >
                  {showDec ? 'Ascunde detalii ▲' : 'Vezi detalii ▾'}
                </button>
              </div>
              {showDec && (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="border-b border-b4l-border text-left text-b4l-text-muted">
                      <tr>
                        <th className="py-1.5 pr-3">DP ID</th>
                        <th className="py-1.5 pr-3">Data plată</th>
                        <th className="py-1.5 pr-3">Perioadă</th>
                        <th className="py-1.5 pr-3 text-right">Linii</th>
                        <th className="py-1.5 text-right">Net (lei)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {settle.settlements.map((s) => (
                        <tr key={s.id} className="border-b border-b4l-border last:border-0">
                          <td className="py-1.5 pr-3 font-mono">{s.dpId}</td>
                          <td className="py-1.5 pr-3">{d(s.payoutDate)}</td>
                          <td className="py-1.5 pr-3 text-b4l-text-muted">
                            {d(s.periodStart)} – {d(s.periodEnd)}
                          </td>
                          <td className="py-1.5 pr-3 text-right">{nr(s.lines)}</td>
                          <td className="py-1.5 text-right font-semibold">{lei(s.net)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}

          {/* Costuri eMAG — component comun (Dashboard + Incasari) */}
          <CostsCard from={from} to={to} />

          {/* Verificare incasari -> contabilitate */}
          {recon && recon.summary.totalOrders > 0 && (
            <Card
              title="Verificare încasări → contabilitate"
              actions={
                <button
                  onClick={() =>
                    void downloadXlsx(
                      `/emag/settlements/reconcile/export${qs()}`,
                      'verificare-incasari-emag.xlsx',
                    )
                  }
                  className="rounded-md border border-b4l-border bg-white px-3 py-1.5 text-xs font-semibold text-b4l-text shadow-sm transition hover:border-b4l-blue hover:text-b4l-blue-dark"
                >
                  Export excepții
                </button>
              }
            >
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                <div className="rounded-lg border-l-4 border-l-b4l-green-dark border-b4l-border bg-b4l-green-light/20 p-3">
                  <div className="truncate text-xs text-b4l-text-muted">OK complet</div>
                  <div className="text-lg font-semibold text-b4l-green-dark">
                    {nr(recon.summary.allOk)}
                  </div>
                  <div className="text-xs text-b4l-text-muted">
                    din {nr(recon.summary.totalOrders)}
                  </div>
                </div>
                <div className="rounded-lg border-l-4 border-l-b4l-red-dark border-b4l-border bg-b4l-red-light/20 p-3">
                  <div className="truncate text-xs text-b4l-text-muted">Încasat fără factură</div>
                  <div className="text-lg font-semibold text-b4l-red-dark">
                    {nr(recon.summary.noInvoice)}
                  </div>
                  <div className="text-xs text-b4l-text-muted">
                    + {nr(recon.summary.refundedWash)} rambursate (normal)
                  </div>
                </div>
                <div className="rounded-lg border-l-4 border-l-b4l-red-dark border-b4l-border bg-b4l-red-light/20 p-3">
                  <div className="truncate text-xs text-b4l-text-muted">Neînreg. în EA</div>
                  <div className="text-lg font-semibold text-b4l-red-dark">
                    {nr(recon.summary.notInEa)}
                  </div>
                </div>
                <div className="rounded-lg border-l-4 border-l-amber-500 border-b4l-border bg-amber-50 p-3">
                  <div className="truncate text-xs text-b4l-text-muted">Netrimisă în SPV</div>
                  <div className="text-lg font-semibold text-amber-700">
                    {nr(recon.summary.notInSpv)}
                  </div>
                </div>
                <div className="rounded-lg border-l-4 border-l-amber-500 border-b4l-border bg-amber-50 p-3">
                  <div className="truncate text-xs text-b4l-text-muted">Dif. valoare</div>
                  <div className="text-lg font-semibold text-amber-700">
                    {nr(recon.summary.valueMismatch)}
                  </div>
                </div>
                <div
                  className="rounded-lg border border-b4l-border bg-b4l-bg-soft/40 p-3"
                  title="Facturi mai vechi decât pornirea sincronizării ANAF. ANAF nu mai dă date peste 60 zile (nici prin portal), deci istoricul SPV e nerecuperabil. NU sunt lipsă reale — doar neevaluabile."
                >
                  <div className="truncate text-xs text-b4l-text-muted">SPV neevaluat (vechi)</div>
                  <div className="text-lg font-semibold text-b4l-text-muted">
                    {nr(recon.summary.spvUnknown)}
                  </div>
                  <div className="text-[11px] text-b4l-text-muted">
                    înainte de pornirea sincronizării ANAF — nu e o problemă
                  </div>
                </div>
                <div className="rounded-lg border border-b4l-border p-3">
                  <div className="truncate text-xs text-b4l-text-muted">
                    Total încasat (deconturi)
                  </div>
                  <div className="text-base font-semibold leading-tight">
                    {lei(recon.summary.cashingTotal)} lei
                  </div>
                </div>
              </div>
              <p className="mt-2 text-[11px] text-b4l-text-muted">
                Pentru fiecare comandă încasată în deconturile importate: există factură de vânzare,
                e înregistrată în EA și trimisă în SPV. OK complet = toate condițiile îndeplinite.
                Încasările rambursate (comenzi anulate/returnate plătite cu card, net 0) nu necesită
                factură și nu sunt marcate ca problemă.{' '}
                <span className="font-medium">Netrimisă SPV</span> se evaluează doar pentru
                facturile de la pornirea sincronizării ANAF încoace; cele mai vechi nu pot fi
                verificate (ANAF nu mai dă date peste 60 zile, nici prin portal — istoricul SPV e
                nerecuperabil) și apar la categoria SPV neevaluat — nu sunt lipsă reale.
              </p>

              {recon.exceptions.length > 0 && (
                <div className="mt-3">
                  <button
                    onClick={() => setShowVerif((v) => !v)}
                    className="text-xs font-semibold text-b4l-blue hover:text-b4l-blue-dark hover:underline"
                  >
                    {showVerif
                      ? 'Ascunde excepțiile ▲'
                      : `Vezi cele ${nr(recon.exceptions.length)} excepții ▾`}
                  </button>
                  {showVerif && (
                    <div className="mt-2 max-h-72 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 border-b border-b4l-border bg-white text-left text-b4l-text-muted">
                          <tr>
                            <th className="py-1 pr-3">Order ID</th>
                            <th className="py-1 pr-3 text-right">Încasat</th>
                            <th className="py-1 pr-3">Status</th>
                            <th className="py-1 pr-3">Factură</th>
                            <th className="py-1">Problemă</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recon.exceptions.slice(0, 200).map((e) => (
                            <tr
                              key={e.orderId}
                              className="border-b border-b4l-border last:border-0"
                            >
                              <td className="py-1 pr-3 font-mono">{e.orderId}</td>
                              <td className="py-1 pr-3 text-right">{lei(e.cashingNet)}</td>
                              <td className="py-1 pr-3 text-b4l-text-muted">
                                {e.orderStatus ?? '—'}
                              </td>
                              <td className="py-1 pr-3 font-mono">{e.invNo ?? '—'}</td>
                              <td className="py-1 text-b4l-red-dark">{e.category}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {recon.exceptions.length > 200 && (
                        <p className="mt-1 text-[11px] text-b4l-text-muted">
                          Primele 200 afișate · {nr(recon.exceptions.length)} total — vezi exportul.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Card>
          )}

          {/* Dashboard Plati (payout-uri) + confirmare bancara */}
          {payouts && payouts.payouts.length > 0 && (
            <Card
              title="Plăți (payout-uri eMAG) → extras bancar"
              actions={
                <div className="flex items-center gap-2">
                  <BackfillPayouts />
                  <button
                    onClick={() =>
                      void downloadXlsx(`/emag/payouts/export${qs()}`, 'plati-emag.xlsx')
                    }
                    className="rounded-md border border-b4l-border bg-white px-3 py-1.5 text-xs font-semibold text-b4l-text shadow-sm transition hover:border-b4l-blue hover:text-b4l-blue-dark"
                  >
                    Export Excel
                  </button>
                </div>
              }
            >
              <div className="mb-2 flex flex-wrap gap-4 text-sm">
                <span>
                  <span className="font-semibold">{nr(payouts.totals.count)}</span> payout-uri
                </span>
                <span>
                  Total plată:{' '}
                  <span className="font-semibold">{lei(payouts.totals.total)} lei</span>
                </span>
                <span>
                  Confirmate în bancă:{' '}
                  <span className="font-semibold text-b4l-green-dark">
                    {nr(payouts.totals.matched)}
                  </span>
                  /{nr(payouts.totals.count)}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="border-b border-b4l-border text-left text-b4l-text-muted">
                    <tr>
                      <th className="py-1.5 pr-3">Payout ID</th>
                      <th className="py-1.5 pr-3">Data</th>
                      <th className="py-1.5 pr-3">Procesator</th>
                      <th className="py-1.5 pr-3 text-right">Vânzări</th>
                      <th className="py-1.5 pr-3 text-right">Vouchere</th>
                      <th className="py-1.5 pr-3 text-right">Comision</th>
                      <th className="py-1.5 pr-3 text-right">FBE</th>
                      <th className="py-1.5 pr-3 text-right">Ads</th>
                      <th className="py-1.5 pr-3 text-right">Alte facturi</th>
                      <th className="py-1.5 pr-3 text-right">Datorie curier</th>
                      <th className="py-1.5 pr-3 text-right">Alte documente</th>
                      <th className="py-1.5 pr-3 text-right">Plăți</th>
                      <th className="py-1.5 pr-3 text-right">Total plată</th>
                      <th className="py-1.5 pr-3">Bancă</th>
                      <th className="py-1.5">EA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.payouts.map((p) => (
                      <Fragment key={p.payoutId}>
                        <tr
                          onClick={() => togglePayout(p.payoutId)}
                          className="cursor-pointer border-b border-b4l-border last:border-0 hover:bg-b4l-bg-soft"
                        >
                          <td className="py-1.5 pr-3 font-mono">
                            <span className="mr-1 inline-block text-b4l-text-muted">
                              {expandedPayouts.has(p.payoutId) ? '▾' : '▸'}
                            </span>
                            {p.payoutId}
                          </td>
                          <td className="py-1.5 pr-3">{d(p.payoutDate)}</td>
                          <td className="py-1.5 pr-3 text-b4l-text-muted">{p.processor ?? '—'}</td>
                          <td className="py-1.5 pr-3 text-right">{lei(Math.abs(p.sales ?? 0))}</td>
                          <td className="py-1.5 pr-3 text-right">{lei(p.vouchers ?? 0)}</td>
                          <td className="py-1.5 pr-3 text-right text-amber-700">
                            {lei(Math.abs(p.commissions ?? 0))}
                          </td>
                          <td className="py-1.5 pr-3 text-right text-amber-700">
                            {lei(Math.abs(p.fbe ?? 0))}
                          </td>
                          <td className="py-1.5 pr-3 text-right text-amber-700">
                            {lei(Math.abs(p.ads ?? 0))}
                          </td>
                          <td className="py-1.5 pr-3 text-right">{lei(p.otherInv ?? 0)}</td>
                          <td className="py-1.5 pr-3 text-right">{lei(p.courier ?? 0)}</td>
                          <td className="py-1.5 pr-3 text-right">{lei(p.otherDoc ?? 0)}</td>
                          <td className="py-1.5 pr-3 text-right">{lei(p.payments ?? 0)}</td>
                          <td className="py-1.5 pr-3 text-right font-semibold">{lei(p.total)}</td>
                          <td className="py-1.5">
                            {p.matched ? (
                              <span className="text-b4l-green-dark">confirmat</span>
                            ) : (
                              <span className="text-amber-700">negăsit</span>
                            )}
                          </td>
                          <td className="py-1.5 pl-3">
                            {p.eaExportatLa ? (
                              <span
                                className="text-b4l-green-dark"
                                title="Fișierul cu note a fost descărcat; importul în EA se face manual, de contabilitate"
                              >
                                fișier {d(p.eaExportatLa)}
                              </span>
                            ) : (
                              <span className="text-b4l-text-muted">—</span>
                            )}
                          </td>
                        </tr>
                        {expandedPayouts.has(p.payoutId) && (
                          <tr className="border-b border-b4l-border bg-b4l-bg-soft">
                            <td colSpan={15} className="px-3 py-2">
                              <PayoutDetail payoutId={p.payoutId} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-[11px] text-b4l-text-muted">
                Datele din pagina Plăți eMAG (lista de payout-uri). Total plată = transferul real,
                căutat în extras ca depunere de la HeyBlu FS / Dante International (±2 lei).
                Vânzări/Comision/FBE/Ads = valori absolute. Negăsit = extrasul nu e încă importat
                sau suma nu se potrivește.
              </p>
              <details className="mt-2 text-[11px] text-b4l-text-muted">
                <summary className="cursor-pointer font-semibold text-b4l-text hover:text-b4l-blue-dark">
                  Cum se leagă de Desfășurătoarele de plată (DP)?
                </summary>
                <div className="mt-1.5 space-y-1.5 border-l-2 border-b4l-border pl-3">
                  <p>
                    Lanțul eMAG:{' '}
                    <span className="font-semibold">
                      Comandă → Desfășurător de plată (DP, decont bilunar) → Payout (transferul de
                      aici) → Extras bancar
                    </span>
                    .
                  </p>
                  <p>
                    <span className="font-semibold">DP</span> = decontul pe ~15 zile, emis{' '}
                    <span className="font-semibold">câte unul per metodă de încasare</span> (de-asta
                    apar 2 rânduri/perioadă în eMAG: unul Card, unul Ramburs/COD).{' '}
                    {'„Valoare fără TVA"'} = cash-ul net încasat de la clienți pe acel canal. Le
                    găsești în Export Excel, tab-ul{' '}
                    <span className="font-semibold">{'„Deconturi DP"'}</span>.
                  </p>
                  <p>
                    <span className="font-semibold">Payout-ul</span> regrupează aceleași încasări,
                    dar pe <span className="font-semibold">momentul transferului</span>, și în plus
                    adună/scade Revolving, comisionul (factura DM), închiderea Revolving și
                    restituirile:{' '}
                    <span className="font-mono">
                      încasări (din DP-uri) + Revolving − comision − închidere Revolving −
                      restituiri = Total plată
                    </span>
                    .
                  </p>
                  <p>
                    De aceea{' '}
                    <span className="font-semibold">DP și payout nu se potrivesc leu-la-leu</span>:
                    sunt două grupări diferite ale acelorași bani (perioadă+metodă vs transfer),
                    plus diferențe de timing (cash-ul unei comenzi dintr-o perioadă poate intra în
                    alt payout).
                  </p>
                </div>
              </details>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
